import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  QrCode, 
  MapPin, 
  FileText, 
  Compass, 
  Home,
  Smartphone,
  Loader2,
  ChevronRight,
  Utensils,
  Landmark,
  Phone,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = 'https://capitol-view-stay.com';

/**
 * Guest Resources Page
 * 
 * A digital display page for guests to scan QR codes and access:
 * - Property-specific Local Guides
 * - General Local Guide
 * - House Rules
 * 
 * Can be accessed at:
 * - /guest-resources (shows all properties)
 * - /guest-resources/:propertyId (shows specific property resources)
 */
const GuestResources = () => {
  const { propertyId } = useParams();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('local-guide');

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/properties`);
        if (!response.ok) throw new Error('Failed to fetch properties');
        const data = await response.json();
        setProperties(data);
        
        // If propertyId is provided, select that property
        if (propertyId) {
          const prop = data.find(p => p.id === propertyId);
          if (prop) setSelectedProperty(prop);
        }
      } catch (err) {
        console.error('Error fetching properties:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [propertyId]);

  // QR Code data for different resources
  const getQRData = () => {
    if (selectedProperty) {
      return {
        localGuide: {
          url: `${BASE_URL}/local-guide/${selectedProperty.id}`,
          title: `Local Guide`,
          subtitle: selectedProperty.name,
          icon: MapPin,
          description: 'Restaurants, attractions & tips near your property'
        },
        houseRules: {
          url: `${BASE_URL}/house-rules`,
          title: 'House Rules',
          subtitle: 'Check-in & Policies',
          icon: FileText,
          description: 'Check-in/out times, policies & important info'
        },
        generalGuide: {
          url: `${BASE_URL}/local-guide`,
          title: 'DC Travel Guide',
          subtitle: 'Explore Washington DC',
          icon: Compass,
          description: 'Complete guide to museums, monuments & more'
        }
      };
    }
    return {
      localGuide: {
        url: `${BASE_URL}/local-guide`,
        title: 'Local Guide',
        subtitle: 'Washington DC',
        icon: MapPin,
        description: 'Restaurants, attractions & tips'
      },
      houseRules: {
        url: `${BASE_URL}/house-rules`,
        title: 'House Rules',
        subtitle: 'Check-in & Policies',
        icon: FileText,
        description: 'Check-in/out times, policies & important info'
      },
      generalGuide: {
        url: `${BASE_URL}/local-guide`,
        title: 'DC Travel Guide',
        subtitle: 'Explore Washington DC',
        icon: Compass,
        description: 'Complete guide to museums, monuments & more'
      }
    };
  };

  const qrData = getQRData();

  const tabs = [
    { id: 'local-guide', label: 'Local Guide', icon: MapPin, data: qrData.localGuide },
    { id: 'house-rules', label: 'House Rules', icon: FileText, data: qrData.houseRules },
    { id: 'dc-guide', label: 'DC Guide', icon: Compass, data: qrData.generalGuide },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab)?.data;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <QrCode className="h-8 w-8 text-amber-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Guest Resources</h1>
          </div>
          <p className="text-slate-400 text-center text-sm md:text-base">
            Scan these QR codes with your phone to access helpful information
          </p>
        </div>
      </div>

      {/* Property Selector (if no specific property) */}
      {!propertyId && properties.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 py-6">
          <label className="block text-sm font-medium text-slate-400 mb-2">
            Select Your Property
          </label>
          <select
            value={selectedProperty?.id || ''}
            onChange={(e) => {
              const prop = properties.find(p => p.id === e.target.value);
              setSelectedProperty(prop || null);
            }}
            className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            data-testid="property-selector"
          >
            <option value="">-- General Resources --</option>
            {properties.map(prop => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Selected Property Display */}
      {selectedProperty && (
        <div className="max-w-4xl mx-auto px-4 pb-4">
          <div className="bg-slate-800/50 rounded-lg p-4 flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img 
                src={selectedProperty.images?.[0]} 
                alt={selectedProperty.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{selectedProperty.name}</h2>
              <p className="text-sm text-slate-400">{selectedProperty.location}</p>
            </div>
            <Home className="h-6 w-6 text-amber-400 flex-shrink-0" />
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main QR Code Display */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card className="bg-white overflow-hidden shadow-2xl">
          <CardContent className="p-0">
            {/* QR Code Header */}
            <div className="bg-slate-900 text-white p-6 text-center">
              {activeTabData && (
                <>
                  <activeTabData.icon className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-xl md:text-2xl font-bold">{activeTabData.title}</h2>
                  <p className="text-slate-400">{activeTabData.subtitle}</p>
                </>
              )}
            </div>

            {/* QR Code */}
            <div className="p-8 flex flex-col items-center">
              <div className="bg-white p-4 rounded-2xl shadow-lg border-4 border-slate-100 mb-6">
                {activeTabData && (
                  <QRCodeSVG
                    value={activeTabData.url}
                    size={220}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#1e293b"
                  />
                )}
              </div>

              {/* Instructions */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 text-slate-600 mb-2">
                  <Smartphone className="h-5 w-5" />
                  <span className="font-medium">Scan with your phone camera</span>
                </div>
                <p className="text-sm text-slate-500 max-w-xs">
                  {activeTabData?.description}
                </p>
              </div>

              {/* Direct Link Button */}
              {activeTabData && (
                <Link to={activeTabData.url.replace(BASE_URL, '')} className="w-full max-w-xs">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                    Open Directly
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links Grid */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h3 className="text-lg font-semibold text-white mb-4 text-center">Quick Access</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to={selectedProperty ? `/local-guide/${selectedProperty.id}` : '/local-guide'}>
            <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 transition-colors cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <Utensils className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">Restaurants</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/local-guide">
            <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 transition-colors cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <Landmark className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">Attractions</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/house-rules">
            <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 transition-colors cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <FileText className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">House Rules</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/contact">
            <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800 transition-colors cursor-pointer h-full">
              <CardContent className="p-4 text-center">
                <Phone className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">Contact Us</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* Contact Footer */}
      <div className="max-w-4xl mx-auto px-4 py-8 border-t border-slate-800">
        <div className="text-center">
          <p className="text-slate-400 text-sm mb-4">Need assistance during your stay?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+15715075650" className="flex items-center gap-2 text-white hover:text-amber-400 transition-colors">
              <Phone className="h-5 w-5" />
              <span>(571) 507-5650</span>
            </a>
            <span className="hidden sm:inline text-slate-600">|</span>
            <a href="mailto:capitolviewstays@gmail.com" className="flex items-center gap-2 text-white hover:text-amber-400 transition-colors">
              <Mail className="h-5 w-5" />
              <span>capitolviewstays@gmail.com</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestResources;
